import {
    Button,
    Divider,
    Dropdown,
    Empty,
    List,
    PageHeader,
    Space,
    Spin,
    Tag,
    Typography,
} from 'antd';
import React, { useRef } from 'react';
import { FaPlus } from 'react-icons/fa';
import { IoMdRefresh } from 'react-icons/io';
import { IoCogSharp } from 'react-icons/io5';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ETourTargetType, ETourType } from '../../../enums/tours.enum';
import { Tour } from '../../../interfaces/tours';

const { Text } = Typography;

interface ToursListProps {
    tours: Tour[];
    loading: boolean;
    loadingMore: boolean;
    hasMore: boolean;
    handleStartCreatingTour: () => void;
    onDeleteTour: (tourId: string) => void;
    updateTour: (tourId: string, updates: Partial<Tour>) => Promise<void>;
    startEditingTour: (tour: Tour) => void;
    fetchTours: () => void;
    fetchMoreTours: () => Promise<void>;
}

const ToursList: React.FC<ToursListProps> = ({
    tours,
    loading,
    loadingMore,
    hasMore,
    handleStartCreatingTour,
    onDeleteTour,
    updateTour,
    startEditingTour,
    fetchTours,
    fetchMoreTours,
}) => {
    const scrollableToursRef = useRef<HTMLDivElement | null>(null);

    const getMenuItems = (tour: Tour) => [
        {
            key: 'edit',
            label: 'Edit Tour',
            onClick: () => startEditingTour(tour),
        },
        {
            key: 'delete',
            label: 'Delete Tour',
            onClick: () => onDeleteTour(tour._id!),
        },
        {
            key: 'status',
            label: tour.isActive ? 'Deactivate Tour' : 'Activate Tour',
            onClick: () => updateTour(tour._id!, { isActive: !tour.isActive }),
        },
    ];

    return (
        <PageHeader
            title="Created Tours"
            style={{ padding: 0 }}
            extra={[
                <Button
                    key="refresh"
                    type="ghost"
                    icon={(
                        <IoMdRefresh
                            size={16}
                            style={{
                                verticalAlign: 'middle',
                            }}
                        />
                    )}
                    className="flex-row center-row-center"
                    onClick={fetchTours}
                />,
                <Button
                    key="create-tour"
                    type="primary"
                    icon={<FaPlus size={14} />}
                    onClick={handleStartCreatingTour}
                    className="flex-row center-row-center"
                />,
            ]}
            footer={(
                <Space direction="vertical" style={{ width: '100%' }}>
                    {
                        loading ? (
                            <Space size={16} className="flex-row start-row-center" align="center" style={{ width: '100%' }}>
                                <Spin size="small" />
                                <Text>Loading tours...</Text>
                            </Space>
                        ) : tours.length === 0 ? (
                            <Empty
                                description="No tours created yet"
                                style={{ margin: '20px 0' }}
                            />
                        ) : (
                            <div
                                id="scrollableTours"
                                ref={scrollableToursRef}
                                style={{
                                    padding: 24,
                                    paddingLeft: 0,
                                    width: '100%',
                                    overflow: 'auto',
                                    height: '75vh',
                                }}
                            >
                                <InfiniteScroll
                                    dataLength={tours.length}
                                    next={fetchMoreTours}
                                    hasMore={hasMore}
                                    loader={loadingMore && <Spin size="small" />}
                                    endMessage={
                                        !loadingMore && (
                                            <Divider plain>
                                                No more tours
                                            </Divider>
                                        )
                                    }
                                    scrollableTarget="scrollableTours"
                                >
                                    <List
                                        dataSource={tours}
                                        loading={loading}
                                        renderItem={(tour) => (
                                            <List.Item
                                                onClick={() => startEditingTour(tour)}
                                                key={tour._id || tour.name}
                                                actions={[
                                                    <Dropdown
                                                        key="menu"
                                                        menu={{ items: getMenuItems(tour) }}
                                                        trigger={['click']}
                                                        placement="bottomRight"
                                                    >
                                                        <Button
                                                            type="link"
                                                            icon={<IoCogSharp size={20} />}
                                                            className="flex-row center-row-center"
                                                            style={{ padding: 0 }}
                                                        />
                                                    </Dropdown>,
                                                ]}
                                            >
                                                <List.Item.Meta
                                                    title={(
                                                        <Space size={4}>
                                                            <Tag color={tour.isActive ? 'green' : 'red'}>
                                                                {tour.isActive ? 'Active' : 'Inactive'}
                                                            </Tag>
                                                            <Text strong>
                                                                {tour.name}
                                                            </Text>
                                                        </Space>
                                                    )}
                                                    description={(
                                                        <Space direction="vertical" size={8}>
                                                            <Text style={{ color: '#888' }}>{tour.description}</Text>
                                                            <Space size="small" wrap>
                                                                {tour.type && (
                                                                    <Space size={4}>
                                                                        <Text style={{ fontSize: 12, color: '#999' }} strong>
                                                                            Type:
                                                                        </Text>
                                                                        <Text style={{ fontSize: 12, color: '#999' }}>
                                                                            {tour.type === ETourType.ON_VISIT ? 'On Visit' : 'On Demand'}
                                                                        </Text>
                                                                    </Space>
                                                                )}
                                                                <Divider type="vertical" />
                                                                <Space size={4}>
                                                                    <Text style={{ fontSize: 12, color: '#999' }} strong>
                                                                        Target:
                                                                    </Text>
                                                                    <Text style={{ fontSize: 12, color: '#999' }}>
                                                                        {tour.targetType === ETourTargetType.COUNTRY ? 'Countries' : 'Workspaces'}
                                                                    </Text>
                                                                </Space>
                                                                <Divider type="vertical" />
                                                                {tour.startUrl && (
                                                                    <Space size={4}>
                                                                        <Text style={{ fontSize: 12, color: '#999' }} strong>
                                                                            Start Page:
                                                                        </Text>
                                                                        <Text style={{ fontSize: 12, color: '#999' }}>
                                                                            {tour.startUrl}
                                                                        </Text>
                                                                    </Space>
                                                                )}
                                                            </Space>
                                                        </Space>
                                                    )}
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </InfiniteScroll>
                            </div>
                        )
                    }
                </Space>
            )}
        />
    );
};

export default ToursList;
